import { CSMType, SupportSystemServiceInterface } from '@src/server';
import {
  Comment,
  Contract,
  ServiceCategory,
  ServiceNowApi,
  ServiceNowCreateBillingRequest,
  ServiceNowCreateIncidentRequest,
  ServiceNowCreateRequest,
  ServiceNowCreateEscalation,
  ServiceNowIncidentResponse,
  ServiceNowIssueDetails,
  ServiceNowResponse,
  ServiceNowUpdateIncidentRequest,
  ServiceNowUpdateRequest,
  TicketRequestType,
  ServiceNowCmdbConfigurationItem,
  ServiceNowIssue,
  ServiceNowCreateCaseRequest,
  ServiceNowCase,
  ServiceNowCreateCaseResponse,
  ServiceNowUpdateEscalation,
  IssueType,
  ServiceNowCreateCaseCommentRequest,
  ServiceNowCaseAttachmentsResponse,
  ServiceNowCaseUploadAttachmentsResponse,
  ServiceNowCaseUploadAttachmentsRequest,
  ServiceNowCaseEscalation,
  ServiceNowUpdateCaseEscalation,
  ServiceNowUpdateCaseEscalationResponse,
  ServiceNowCreateCaseQuestionPURequest,
  ServiceNowCreateCaseEscalation,
  ServiceNowCreateCaseEscalationResponse,
  ContractEntitlement,
} from 'cmp-client-api';
import { paramCase } from 'param-case';

import { Ticket } from '@src/domain/pages/support/tickets/tickets-dashboard/types/ticket.type';
import { Case } from '@src/domain/pages/support/cases/cases-dashboard/types/case.type';
import { subcategoriesFields } from '@src/domain/pages/support/cases/cases-create/model/case-create-model';
import { MapperInterface } from '../converters/types/mapper.interface';

export class SupportSystemService implements SupportSystemServiceInterface {
  private readonly service: ServiceNowApi;

  private readonly ticketMapper: MapperInterface<ServiceNowIssue, Ticket>;

  private readonly caseMapper: MapperInterface<ServiceNowCase, Case>;

  public constructor(
    service: ServiceNowApi,
    ticketMapper: MapperInterface<ServiceNowIssue, Ticket>,
    caseMapper: MapperInterface<ServiceNowCase, Case>
  ) {
    this.service = service;
    this.ticketMapper = ticketMapper;
    this.caseMapper = caseMapper;
  }

  public async getTicket(ticketNumber: string): Promise<Ticket> {
    return this.service
      .getServiceNowIssueByNumber(ticketNumber)
      .then(({ data }) => this.ticketMapper.transformToApiModel(data));
  }

  public async listTickets(requestType: TicketRequestType): Promise<Ticket[]> {
    return this.service
      .getServiceNowIssues(requestType)
      .then(({ data }) => data.map(this.ticketMapper.transformToApiModel));
  }

  public async getCase(caseNumber: string): Promise<Case> {
    return this.service
      .getServiceNowCaseByTicketNumber(caseNumber)
      .then(({ data }) => this.caseMapper.transformToApiModel(data));
  }

  public async getCaseEscalation(escalationId: string): Promise<ServiceNowCaseEscalation> {
    return this.service.getServiceNowCaseEscalationByNumber(escalationId).then(({ data }) => data);
  }

  public async listCases(requestType: TicketRequestType): Promise<Case[]> {
    return this.service
      .getServiceNowCases(requestType)
      .then(({ data }) =>
        data?.elements
          ? data.elements.map((csmCase) => this.caseMapper.transformToApiModel(csmCase))
          : []
      );
  }

  public async createIncident(
    incidentRequest: ServiceNowCreateIncidentRequest
  ): Promise<ServiceNowIncidentResponse> {
    return this.service
      .createServiceNowIncident(undefined, incidentRequest)
      .then(({ data }) => data);
  }

  public async createRequest(
    createRequestBody: ServiceNowCreateRequest
  ): Promise<ServiceNowResponse> {
    return this.service
      .createServiceNowRequest(undefined, createRequestBody)
      .then(({ data }) => data);
  }

  public async createCase(
    createCaseRequestBody: ServiceNowCreateCaseRequest
  ): Promise<ServiceNowCreateCaseResponse> {
    return this.service
      .createServiceNowCase(undefined, createCaseRequestBody)
      .then(({ data }) => data);
  }

  public async createBilling(
    createBillingRequestBody: ServiceNowCreateBillingRequest
  ): Promise<ServiceNowResponse> {
    return this.service
      .createServiceNowBillingRequest(undefined, createBillingRequestBody)
      .then(({ data }) => data);
  }

  public async createEscalation(
    escalation: ServiceNowCreateEscalation
  ): Promise<ServiceNowResponse> {
    return this.service.createServiceNowEscalation(undefined, escalation).then(({ data }) => data);
  }

  public async createCaseEscalation(
    escalation: ServiceNowCreateCaseEscalation
  ): Promise<ServiceNowCreateCaseEscalationResponse> {
    return this.service
      .createServiceNowCaseEscalation(undefined, escalation)
      .then(({ data }) => data);
  }

  public async updateEscalationComments(
    escalation: ServiceNowUpdateEscalation
  ): Promise<ServiceNowResponse> {
    return this.service.updateServiceNowEscalation(undefined, escalation).then(({ data }) => data);
  }

  public async listServices(): Promise<Contract[]> {
    return this.service.getServiceNowContracts(undefined).then(({ data }) => data);
  }

  public async getServiceCategories(
    serviceName: string,
    serviceType: IssueType & CSMType
  ): Promise<{ [key: string]: ServiceCategory }> {
    if ((serviceType as IssueType) === IssueType.Incident) {
      return this.service.getServiceCategories(serviceName, 'incident').then(({ data }) => data);
    }

    if ((serviceType as IssueType) === IssueType.Request) {
      return this.service
        .getServiceCategories(
          serviceName,
          paramCase(subcategoriesFields.serviceRequest, {
            delimiter: ' ',
          })
        )
        .then(({ data }) => data);
    }

    if ((serviceType as CSMType) === CSMType.Case) {
      return this.service
        .getServiceNowCsmCategories(
          serviceName,
          paramCase(subcategoriesFields.serviceRequest, {
            delimiter: ' ',
          })
        )
        .then(({ data }) => data);
    }

    return undefined;
  }

  public async getTicketComments(ticketId: string): Promise<Comment[]> {
    return this.service.getServiceNowTicketComments(ticketId).then(({ data }) => data);
  }

  public async createCaseComment(
    request: ServiceNowCreateCaseCommentRequest
  ): Promise<ServiceNowCreateCaseResponse> {
    return this.service.createCaseComment(undefined, request).then(({ data }) => data);
  }

  public async createCaseEscalationComment(
    request: ServiceNowUpdateCaseEscalation
  ): Promise<ServiceNowUpdateCaseEscalationResponse> {
    return this.service.updateServiceNowCaseEscalation(undefined, request).then(({ data }) => data);
  }

  public async getTicketDetails(ticketId: string): Promise<ServiceNowIssueDetails> {
    return this.service.getServiceNowTicketDetails(ticketId).then(({ data }) => data);
  }

  public async getCaseAttachments(caseId: string): Promise<ServiceNowCaseAttachmentsResponse> {
    return this.service.getServiceNowCaseAttachments(caseId).then(({ data }) => data);
  }

  public async getCaseAttachmentDownload(caseLink: string): Promise<string> {
    return this.service
      .getServiceNowCaseAttachmentDownload(caseLink)
      .then(({ data }) => data.location);
  }

  public async uploadCaseAttachments(
    request: ServiceNowCaseUploadAttachmentsRequest
  ): Promise<ServiceNowCaseUploadAttachmentsResponse> {
    return this.service.uploadAttachment(undefined, undefined, request).then(({ data }) => data);
  }

  public async updateTicketComments(
    serviceType: IssueType & CSMType,
    requestPayload: ServiceNowUpdateRequest | ServiceNowUpdateIncidentRequest
  ): Promise<ServiceNowIncidentResponse | ServiceNowResponse> {
    if ((serviceType as IssueType) === IssueType.Incident) {
      return this.service
        .updateServiceNowIncident(undefined, requestPayload as ServiceNowUpdateIncidentRequest)
        .then(({ data }) => data);
    }

    if ((serviceType as IssueType) === IssueType.Request) {
      return this.service
        .updateServiceNowRequest(undefined, requestPayload as ServiceNowUpdateRequest)
        .then(({ data }) => data);
    }

    throw new Error(
      `Invalid request method used! updateTicketComments() supports only types: [${IssueType.Incident},${IssueType.Request}]`
    );
  }

  public async downloadTicketAttachment(fileSnowUrl: string): Promise<string> {
    return this.service.getServiceNowFileAttachment(fileSnowUrl).then(({ data }) => data.location);
  }

  public async createCaseQuestionPU(
    createCaseQuestionPURequestBody: ServiceNowCreateCaseQuestionPURequest
  ): Promise<ServiceNowCreateCaseResponse> {
    return this.service
      .createCaseQuestionPU(undefined, createCaseQuestionPURequestBody)
      .then(({ data }) => data);
  }

  public async listEntitlements(contractId: string): Promise<ContractEntitlement[]> {
    return this.service.getContractEntitlements(contractId).then(({ data }) => data);
  }

  public async getCaseConfigurationItems(): Promise<ServiceNowCmdbConfigurationItem[]> {
    return this.service.getCaseCmdbConfigurationItems().then(({ data }) => data);
  }
}
